// src/Shortcode.jsx
import { lazy, Suspense, useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import LoadingSpinner from './components/LoadingSpinner';
import { ErrorBoundary } from '@sentry/react';
import ErrorComponent from './ErrorComponent';
import { useConfig } from './contexts/ConfigContext';
import { useAppState } from './contexts/AppStateContext';

// Direct import for core components (no lazy loading to prevent cascade)
import Menu from './views/Menu';
import SingleProduct from './views/SingleProduct';
import SweedSpike from './views/SweedSpike'; // THROWAWAY spike (activated by ?sweed=1)

// THROWAWAY spike flag — render Sweed data path when ?sweed=1 is present.
// HARD-DISABLED for production (Sweed is in early development). Returns false
// unconditionally so the spike can never render on a live site, regardless of the
// ?sweed=1 URL param. Restore the original body below to resume spike development:
//   try {
//       return new URLSearchParams(window.location.search).get('sweed') === '1';
//   } catch (e) { return false; }
const isSweedSpike = () => {
    return false;
};

// Lazy load non-critical components
const Cart = lazy(() => import('./views/Cart'));
const Checkout = lazy(() => import('./views/Checkout'));
const OrderConfirmation = lazy(() => import('./views/OrderConfirmation'));
const ShopNav = lazy(() => import('./views/ShopNav'));
const ShopNavFull = lazy(() => import('./views/ShopNavFull'));
const ProductsView = lazy(() => import('./views/ProductsView'));
const MenuButton = lazy(() => import('./views/MenuButton'));

const Shortcode = ({
    shortcodeData,
    rootId,
    forcedOrderType,
}) => {
    const params = useParams();
    
    // Get from ConfigContext (static data)
    const { 
        nonHeadlessRetailers // Only static config from here
    } = useConfig();
    
    // Get from AppStateContext (dynamic state)
    const {
        retailerId,
        setRetailerId,
        nonHeadlessRetailer,
        productPopupData,
        setProductPopupData,
        fallbackEnabled
    } = useAppState();
    
    const { shortcode } = shortcodeData;
    const componentMounted = useRef(false);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const cleanupAttempted = useRef(false);

    // Cleanup loading spinner
    useEffect(() => {
        if (cleanupAttempted.current) return;

        componentMounted.current = true;
        cleanupAttempted.current = true;

        const cleanupSpinner = () => {
            if (!componentMounted.current) return;

            try {
                const key = rootId.split('-').pop();
                const backendLoadingSpinner = document.getElementById(`loading-${key}`);

                if (backendLoadingSpinner) {
                    backendLoadingSpinner.remove();
                }

                setIsLoading(false);
            } catch (error) {
                setIsLoading(false);
            }
        };

        // Small delay to ensure DOM is ready
        setTimeout(cleanupSpinner, 0);

        return () => {
            componentMounted.current = false;
        };
    }, [rootId]);

    // Validation of required props
    useEffect(() => {
        if (!shortcodeData || !rootId) {
            const error = new Error('Missing required props for Shortcode component');
            setError(error);
        }
    }, [shortcodeData, rootId]);

    if (error) {
        return <ErrorComponent error={error} />;
    }

    const renderComponent = () => {
        try {
            switch (shortcode) {
                case 'headlessmenu':
                    return params?.categoryType === 'product' ? (
                        <ErrorBoundary
                            fallback={(props) =>
                                <ErrorComponent
                                    {...props}
                                    componentName="SingleProduct"
                                    parentName="Shortcode"
                                />
                            }
                        >
                            {/* No Suspense - SingleProduct handles its own loading */}
                            <SingleProduct />
                        </ErrorBoundary>
                    ) : (
                        <ErrorBoundary
                            fallback={(props) =>
                                <ErrorComponent
                                    {...props}
                                    componentName="Menu"
                                    parentName="Shortcode"
                                />
                            }
                        >
                            {/* No Suspense - Menu handles its own loading with precise logic */}
                            <Menu
                                rootId={rootId}
                                forcedOrderType={forcedOrderType}
                            />
                        </ErrorBoundary>
                    );
                    
                case 'headlessproducts':
                    // THROWAWAY spike: render the Sweed data path when ?sweed=1 is present.
                    if (isSweedSpike()) {
                        return (
                            <ErrorBoundary
                                fallback={(props) =>
                                    <ErrorComponent {...props} componentName="SweedSpike" parentName="Shortcode" />
                                }
                            >
                                <SweedSpike shortcodeData={shortcodeData} />
                            </ErrorBoundary>
                        );
                    }
                    return (
                        <ErrorBoundary
                            fallback={(props) =>
                                <ErrorComponent
                                    {...props}
                                    componentName="ProductsView"
                                    parentName="Shortcode"
                                />
                            }
                        >
                            <Suspense fallback={<LoadingSpinner />}>
                                <ProductsView
                                    shortcodeData={shortcodeData}
                                />
                            </Suspense>
                        </ErrorBoundary>
                    );
                    
                case 'headlesscart':
                    return (
                        <ErrorBoundary
                            fallback={(props) =>
                                <ErrorComponent
                                    {...props}
                                    componentName="Cart"
                                    parentName="Shortcode"
                                />
                            }
                        >
                            <Suspense fallback={<LoadingSpinner />}>
                                <Cart />
                            </Suspense>
                        </ErrorBoundary>
                    );
                    
                case 'headlesscheckout':
                    return (
                        <ErrorBoundary
                            fallback={(props) =>
                                <ErrorComponent
                                    {...props}
                                    componentName="Checkout"
                                    parentName="Shortcode"
                                />
                            }
                        >
                            <Suspense fallback={<LoadingSpinner />}>
                                <Checkout />
                            </Suspense>
                        </ErrorBoundary>
                    );
                    
                case 'orderconfirmation':
                    return (
                        <ErrorBoundary
                            fallback={(props) =>
                                <ErrorComponent
                                    {...props}
                                    componentName="OrderConfirmation"
                                    parentName="Shortcode"
                                />
                            }
                        >
                            <Suspense fallback={<LoadingSpinner />}>
                                <OrderConfirmation />
                            </Suspense>
                        </ErrorBoundary>
                    );
                    
                case 'shopnav':
                    return (
                        <ErrorBoundary
                            fallback={(props) =>
                                <ErrorComponent
                                    {...props}
                                    componentName="ShopNav"
                                    parentName="Shortcode"
                                />
                            }
                        >
                            <Suspense fallback={<LoadingSpinner loadingType='shopNav' />}>
                                <ShopNav />
                            </Suspense>
                        </ErrorBoundary>
                    );

                case 'shopnavfull':
                    return (
                        <ErrorBoundary
                            fallback={(props) =>
                                <ErrorComponent
                                    {...props}
                                    componentName="ShopNavFull"
                                    parentName="Shortcode"
                                />
                            }
                        >
                            <Suspense fallback={<LoadingSpinner loadingType='shopNav' />}>
                                <ShopNavFull
                                    shortcodeData={shortcodeData}
                                />
                            </Suspense>
                        </ErrorBoundary>
                    );
                    
                case 'headlessmenubutton':
                    return (
                        <ErrorBoundary
                            fallback={(props) =>
                                <ErrorComponent
                                    {...props}
                                    componentName="MenuButton"
                                    parentName="Shortcode"
                                />
                            }
                        >
                            <Suspense fallback={<LoadingSpinner />}>
                                <MenuButton
                                    shortcodeData={shortcodeData}
                                />
                            </Suspense>
                        </ErrorBoundary>
                    );
                    
                default:
                    throw new Error(`Unknown shortcode type: ${shortcode}`);
            }
        } catch (error) {
            return <ErrorComponent error={error} />;
        }
    };

    if (isLoading) {
        // Show appropriate loading spinner based on shortcode type
        const getLoadingType = () => {
            switch (shortcode) {
                case 'shopnav':
                case 'shopnavfull':
                    return 'shopNav';
                case 'headlessmenu':
                    return 'menuList';
                default:
                    return 'list';
            }
        };

        const loadingType = getLoadingType();
        return <LoadingSpinner loadingType={loadingType} />;
    }

    return (
        <ErrorBoundary
            showDialog
            fallback={(props) => <ErrorComponent {...props} />}
            beforeCapture={(scope) => {
                scope.setTag('shortcode', shortcode);
                scope.setContext('componentData', {
                    rootId,
                    shortcodeType: shortcode,
                    hasRetailer: !!nonHeadlessRetailer,
                    shortcodeData
                });
            }}
        >
            {renderComponent()}
        </ErrorBoundary>
    );
};

export default Shortcode;
